import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Award, ArrowLeft, Filter, CheckSquare, Eye } from 'lucide-react';

const TestOfDayResults = () => {
  const { testId } = useParams(); // testId is the test-of-the-day ID
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [testDetails, setTestDetails] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [testOfDayDetails, setTestOfDayDetails] = useState(null);
  const [sortBy, setSortBy] = useState('rank'); // rank, name, score
  const [filterText, setFilterText] = useState('');

  const API_BASE_URL = 'https://apissc.sainikschoolcadet.com/api';

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch test of the day details
        const testOfDayResponse = await fetch(`${API_BASE_URL}/test-of-the-day/${testId}`);
        
        if (!testOfDayResponse.ok) {
          throw new Error('Failed to fetch test of the day details');
        }
        
        const testOfDayData = await testOfDayResponse.json();
        setTestOfDayDetails(testOfDayData);

        if (testOfDayData) {
          // Fetch test details using the actual test_id
          const testResponse = await fetch(`${API_BASE_URL}/tests/${testOfDayData.test_id}`);
          
          if (!testResponse.ok) {
            throw new Error('Failed to fetch test details');
          }
          
          const testDetailsData = await testResponse.json();
          setTestDetails(testDetailsData);
        }

        // Fetch test results data using the test-of-the-day ID as source ID
        try {
          // Using the same API endpoint format as in the batch test results
          const resultsResponse = await fetch(`${API_BASE_URL}/student-test-records/type/TEST_OF_THE_DAY/source/${testId}`);
          
          if (!resultsResponse.ok) {
            // If no records are found, set empty leaderboard
            if (resultsResponse.status === 404) {
              setLeaderboardData([]);
              return;
            }
            throw new Error('Failed to fetch test results');
          }
          
          const resultsData = await resultsResponse.json();
          
          if (resultsData.success && resultsData.data && resultsData.data.length > 0) {
            // Process records and fetch student names
            const recordsWithStudentInfo = await Promise.all(
              resultsData.data.map(async (result) => {
                try {
                  // Using s_id instead of student_id to match the API response format
                  const studentResponse = await fetch(`${API_BASE_URL}/Students/getbyid/${result.s_id}`);
                  
                  if (!studentResponse.ok) {
                    throw new Error(`Failed to fetch student with ID ${result.s_id}`);
                  }
                  
                  const studentData = await studentResponse.json();
                  
                  return {
                    ...result,
                    student_name: studentData?.f_name || 'Unknown Student',
                    student_id: result.s_id, // Map s_id to student_id for consistency
                    marks_obtained: result.marks_scored, // Map marks_scored to marks_obtained for consistency
                    attempted_at: result.attempted_at, // Use attempted_at as submission time
                    status: result.marks_scored < result.total_marks * 0.4 ? 'failed' : 'completed' // Add status based on score
                  };
                } catch (error) {
                  console.error(`Error fetching student ${result.s_id}:`, error);
                  return {
                    ...result,
                    student_name: 'Unknown Student',
                    student_id: result.s_id,
                    marks_obtained: result.marks_scored,
                    attempted_at: result.attempted_at,
                    status: result.marks_scored < result.total_marks * 0.4 ? 'failed' : 'completed'
                  };
                }
              })
            );

            // Sort data by score (descending) to create rankings
            const processedData = recordsWithStudentInfo
              .sort((a, b) => (b.marks_scored || 0) - (a.marks_scored || 0))
              .map((result, index) => ({
                ...result,
                rank: index + 1,
                score: result.marks_scored || 0,
                total_marks: result.total_marks || 0,
                percentage: result.total_marks ? ((result.marks_scored / result.total_marks) * 100).toFixed(2) : '0.00',
                submitted_at: result.attempted_at,
                time_taken_mins: Math.floor(result.time_taken / 60),
                time_taken_secs: result.time_taken % 60
              }));

            setLeaderboardData(processedData);
          } else {
            // No records found
            setLeaderboardData([]);
          }
        } catch (resultsError) {
          console.error('Error fetching leaderboard results:', resultsError);
          // Handle API error response
          if (resultsError.response && resultsError.response.data && resultsError.response.data.message === "No records found") {
            setLeaderboardData([]);
          } else {
            throw resultsError;
          }
        }

      } catch (err) {
        console.error('Error fetching leaderboard data:', err);
        setError('Failed to load test data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [testId]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Not specified';
    return new Date(dateString).toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format time taken
  const formatTimeTaken = (seconds) => {
    if (!seconds && seconds !== 0) return 'Not recorded';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Sort data
  const sortedData = () => {
    let sorted = [...leaderboardData];
    
    switch (sortBy) {
      case 'name':
        sorted.sort((a, b) => a.student_name.localeCompare(b.student_name));
        break;
      case 'score':
        sorted.sort((a, b) => b.score - a.score);
        break;
      case 'rank':
      default:
        sorted.sort((a, b) => a.rank - b.rank);
        break;
    }

    // Apply filter
    if (filterText) {
      sorted = sorted.filter(item => 
        item.student_name.toLowerCase().includes(filterText.toLowerCase()) ||
        item.student_id?.toString().includes(filterText)
      );
    }

    return sorted;
  };

  // Loading state
  if (loading) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <h2 className="text-xl font-bold mb-4">Test of the Day Results</h2>
        <div className="flex justify-center items-center h-64">
          <div className="animate-pulse flex flex-col items-center">
            <div className="w-12 h-12 rounded-full bg-gray-200 mb-3"></div>
            <p className="text-xl text-gray-600">Loading results...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
        <h2 className="text-xl font-bold mb-4">Test of the Day Results</h2>
        <div className="mt-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
          {error}
        </div>
        <button 
          onClick={() => navigate('/test-of-the-day')}
          className="mt-4 px-4 py-2 rounded-md bg-blue-600 text-white text-sm inline-flex items-center gap-2"
        >
          <ArrowLeft size={16} /> Back to Test of the Day
        </button>
      </div>
    );
  }

  const filteredAndSortedData = sortedData();

  return (
    <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <button 
              onClick={() => navigate('/test-of-the-day')}
              className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="mr-2" /> Back to Test of the Day
            </button>
            
            <h2 className="text-xl font-bold mb-2">Test of the Day Results</h2>
            
            <div className="mt-2 space-y-1">
              <p className="text-gray-600">Test: {testOfDayDetails?.title || 'Unknown Test'}</p>
              <p className="text-gray-600">Test Date: {formatDate(testOfDayDetails?.test_date)}</p>
              <p className="text-gray-600">Created On: {formatDate(testOfDayDetails?.created_at)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Sort */}
      <div className="mb-6 flex flex-wrap gap-4">
        <div className="flex items-center gap-2">
          <Filter className="text-gray-500" size={16} />
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="rank">Sort by Rank</option>
          <option value="name">Sort by Name</option>
          <option value="score">Sort by Score</option>
        </select>
      </div>

      {/* Leaderboard Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                S.No
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rank
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Student Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Score
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Percentage
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Time Taken
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Submission Time
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                View
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredAndSortedData.map((result, index) => (
              <tr key={result.id} className={`hover:bg-gray-50 ${result.rank <= 3 ? 'bg-yellow-50' : ''}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">
                    {index + 1}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="flex items-center">
                    {result.rank <= 3 && (
                      <Award 
                        className={`mr-2 ${
                          result.rank === 1 ? 'text-yellow-500' :
                          result.rank === 2 ? 'text-gray-400' :
                          'text-orange-600'
                        }`} 
                        size={16}
                      />
                    )}
                    <span className="text-sm font-medium text-gray-900">
                      {result.rank}
                    </span>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{result.student_name}</div>
                  <div className="text-xs text-gray-500">ID: {result.student_id}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {result.score} / {result.total_marks}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">{result.percentage}%</div>
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-1">
                    <div 
                      className={`h-2 rounded-full ${
                        parseFloat(result.percentage) >= 70 ? 'bg-green-600' :
                        parseFloat(result.percentage) >= 40 ? 'bg-blue-600' :
                        'bg-red-600'
                      }`}
                      style={{ width: `${result.percentage}%` }}
                    ></div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {formatTimeTaken(result.time_taken)}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(result.submitted_at)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    type="button"
                    onClick={() => window.open(`/view-student/${result.student_id}`, '_blank', 'noopener,noreferrer')}
                    className="inline-flex items-center p-2 rounded-md hover:bg-gray-100 text-gray-600 hover:text-gray-900"
                    title="View Student Profile"
                  >
                    <Eye size={18} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredAndSortedData.length === 0 && (
          <div className="text-center py-12 bg-gray-50">
            <CheckSquare className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No test results yet</h3>
            <p className="mt-1 text-sm text-gray-500">
              No students have attempted this test yet.
            </p>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => navigate('/test-of-the-day')}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <ArrowLeft className="-ml-1 mr-2 h-5 w-5" />
                Back to Tests
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Summary Stats */}
      {leaderboardData.length > 0 && (
        <div className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-blue-800">Total Students</h3>
            <p className="text-2xl font-bold text-blue-900">{leaderboardData.length}</p>
          </div>
          <div className="bg-green-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-green-800">Average Score</h3>
            <p className="text-2xl font-bold text-green-900">
              {(leaderboardData.reduce((acc, curr) => acc + curr.score, 0) / leaderboardData.length).toFixed(2)}
            </p>
          </div>
          <div className="bg-yellow-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-yellow-800">Highest Score</h3>
            <p className="text-2xl font-bold text-yellow-900">
              {Math.max(...leaderboardData.map(item => item.score))}
            </p>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-purple-800">Pass Rate</h3>
            <p className="text-2xl font-bold text-purple-900">
              {((leaderboardData.filter(item => item.percentage >= 40).length / leaderboardData.length) * 100).toFixed(1)}%
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestOfDayResults;